import {
  ActivationCriteria,
  ActivationCriteriaCategory,
  CompletionCriteria,
  CompletionCriteriaCategory,
  MemberKind,
  MemberRole,
  MemberTarget,
  NestedCriteria,
  StructuredTaskGroupDto,
  StructuredTaskItemDto,
  TaskItemMember,
} from "dtos/tasks/task-template.dto";
import { T_MongoId } from "types/_utils.types";

export type CriteriaTone = "neutral" | "accent" | "warning" | "error";

export type TaskNode = {
  task: StructuredTaskItemDto;
  seq: string;
  position: number;
  depth: 0 | 1;
  groupId: string;
  groupName: string;
  groupCode: string;
  parentId: string | null;
  previousId: string | null;
};

export type TaskIndex = Map<string, TaskNode>;

export type DependencyKind = "previous" | "parent" | "outcome";

export type ResolvedDependency = {
  kind: DependencyKind;
  requirement: string;
  node?: TaskNode;
  outcomeId?: string;
  tone: CriteriaTone;
  note?: string;
};

export type ActivationRule = {
  mode: "all" | "any";
  required: number;
  total: number;
  nestedGroups: number;
  items: ResolvedDependency[];
};

export type MemberAction = {
  role: MemberRole;
  member?: TaskItemMember;
  outcomeId: string;
  tone: CriteriaTone;
  note?: string;
};

export type CompletionRule = {
  mode: "all" | "any";
  required: number;
  total: number;
  nestedGroups: number;
  subtaskGate: boolean;
  subtasks: TaskNode[];
  memberActions: MemberAction[];
};

export type TemplateIssue = {
  taskId: string;
  code: string;
  severity: "error" | "warning";
  message: string;
};

const asId = (value: T_MongoId | string | null | undefined) => (value ? String(value) : null);

const pad = (n: number) => String(n).padStart(2, "0");

const byOrder = (a: StructuredTaskItemDto, b: StructuredTaskItemDto) => a.order - b.order;

const isNested = <T extends { category: string }>(value: NestedCriteria<T> | T): value is NestedCriteria<T> =>
  "options" in value;

const collectLeaves = <T extends { category: string }>(criteria: NestedCriteria<T>) => {
  const leaves: T[] = [];
  let nestedGroups = 0;

  const walk = (current: NestedCriteria<T>) => {
    current.options.forEach((option) => {
      if (isNested(option)) {
        nestedGroups += 1;
        walk(option);
        return;
      }
      leaves.push(option);
    });
  };

  walk(criteria);

  return { leaves, nestedGroups };
};

export const flattenTemplate = (groups: StructuredTaskGroupDto[]): TaskNode[] => {
  const nodes: TaskNode[] = [];
  let position = 0;

  groups.forEach((group) => {
    const roots = [...(group.tasks ?? [])].sort(byOrder);

    roots.forEach((task, rootIndex) => {
      position += 1;
      const seq = pad(position);

      nodes.push({
        task,
        seq,
        position,
        depth: 0,
        groupId: String(group._id),
        groupName: group.name,
        groupCode: group.code,
        parentId: null,
        previousId: rootIndex > 0 ? asId(roots[rootIndex - 1]._id) : null,
      });

      const subtasks = [...(task.subtasks ?? [])].sort(byOrder);

      subtasks.forEach((subtask, subIndex) => {
        nodes.push({
          task: subtask,
          seq: `${seq}.${subIndex + 1}`,
          position,
          depth: 1,
          groupId: String(group._id),
          groupName: group.name,
          groupCode: group.code,
          parentId: asId(task._id),
          previousId: subIndex > 0 ? asId(subtasks[subIndex - 1]._id) : null,
        });
      });
    });
  });

  return nodes;
};

export const indexTasks = (nodes: TaskNode[]): TaskIndex => new Map(nodes.map((node) => [String(node.task._id), node]));

export const subtaskNodes = (node: TaskNode, index: TaskIndex): TaskNode[] =>
  [...(node.task.subtasks ?? [])]
    .sort(byOrder)
    .map((subtask) => index.get(String(subtask._id)))
    .filter((value): value is TaskNode => Boolean(value));

export const memberFor = (task: StructuredTaskItemDto, role: MemberRole) =>
  task.members?.find((member) => member.role === role);

export const isMemberResolved = (member?: TaskItemMember) => {
  if (!member) return false;
  if (member.kind === MemberKind.EXTERNAL) return true;
  return member.target === MemberTarget.ROLE ? Boolean(member.assigneeRole) : Boolean(member.assigneeId);
};

export const describeMember = (member: TaskItemMember) => {
  if (member.target === MemberTarget.ROLE) {
    return member.assigneeRole ? `Role: ${member.assigneeRole}` : "Role not set";
  }
  return member.assigneeId ? "Assigned" : "Unassigned";
};

const resolveActivationLeaf = (leaf: ActivationCriteria, node: TaskNode, index: TaskIndex): ResolvedDependency => {
  if (leaf.category === ActivationCriteriaCategory.PARENT_TASK_ACTIVE) {
    const parent = node.parentId ? index.get(node.parentId) : undefined;

    return {
      kind: "parent",
      requirement: "must be active",
      node: parent,
      tone: "neutral",
      note: parent ? undefined : "Top-level task — no parent to wait on",
    };
  }

  if (leaf.category === ActivationCriteriaCategory.PREVIOUS_TASK_COMPLETE) {
    const previous = node.previousId ? index.get(node.previousId) : undefined;

    return {
      kind: "previous",
      requirement: "must be complete",
      node: previous,
      tone: "neutral",
      note: previous ? undefined : "First in its chain — nothing to wait on",
    };
  }

  const source = index.get(String((leaf as any).task));
  const outcomeId = String((leaf as any).outcome);

  if (!source) {
    return {
      kind: "outcome",
      requirement: "must record this outcome",
      outcomeId,
      tone: "error",
      note: "waits on a task that is not in this template",
    };
  }

  if (source.position > node.position) {
    return {
      kind: "outcome",
      requirement: "must record this outcome",
      node: source,
      outcomeId,
      tone: "warning",
      note: `${source.seq} appears after this task — intended only if the flow loops back`,
    };
  }

  return { kind: "outcome", requirement: "must record this outcome", node: source, outcomeId, tone: "accent" };
};

export const resolveActivation = (node: TaskNode, index: TaskIndex): ActivationRule | null => {
  const criteria = node.task.activationCriteria;
  if (!criteria || criteria.options.length === 0) return null;

  const { leaves, nestedGroups } = collectLeaves(criteria);

  return {
    mode: criteria.minimumRequired >= criteria.options.length ? "all" : "any",
    required: criteria.minimumRequired,
    total: criteria.options.length,
    nestedGroups,
    items: leaves.map((leaf) => resolveActivationLeaf(leaf, node, index)),
  };
};

export const resolveCompletion = (node: TaskNode, index: TaskIndex): CompletionRule => {
  const criteria = node.task.completionCriteria;

  if (!criteria || criteria.options.length === 0) {
    return { mode: "all", required: 0, total: 0, nestedGroups: 0, subtaskGate: false, subtasks: [], memberActions: [] };
  }

  const { leaves, nestedGroups } = collectLeaves(criteria);
  const subtaskGate = leaves.some(
    (leaf: CompletionCriteria) => leaf.category === CompletionCriteriaCategory.SUB_TASKS_COMPLETED,
  );

  const memberActions: MemberAction[] = leaves
    .filter(
      (leaf): leaf is Extract<CompletionCriteria, { category: CompletionCriteriaCategory.ACTION_BY_MEMBER }> =>
        leaf.category === CompletionCriteriaCategory.ACTION_BY_MEMBER,
    )
    .map((leaf) => {
      const member = memberFor(node.task, leaf.member);

      if (!member) {
        return {
          role: leaf.member,
          outcomeId: String(leaf.outcome),
          tone: "error" as CriteriaTone,
          note: `No ${leaf.member} on this task, so no one can complete it`,
        };
      }

      return {
        role: leaf.member,
        member,
        outcomeId: String(leaf.outcome),
        tone: isMemberResolved(member) ? ("accent" as CriteriaTone) : ("warning" as CriteriaTone),
        note: isMemberResolved(member) ? undefined : "Resolved when the board is created",
      };
    });

  return {
    mode: criteria.minimumRequired >= criteria.options.length ? "all" : "any",
    required: criteria.minimumRequired,
    total: criteria.options.length,
    nestedGroups,
    subtaskGate,
    subtasks: subtaskGate ? subtaskNodes(node, index) : [],
    memberActions,
  };
};

export const validateTemplate = (nodes: TaskNode[], index: TaskIndex): TemplateIssue[] => {
  const issues: TemplateIssue[] = [];

  nodes.forEach((node) => {
    const taskId = String(node.task._id);
    const activation = node.task.activationCriteria;
    const completion = node.task.completionCriteria;

    if (activation && activation.minimumRequired > activation.options.length) {
      issues.push({
        taskId,
        code: node.seq,
        severity: "error",
        message: `${node.seq} requires ${activation.minimumRequired} activation conditions but has ${activation.options.length}`,
      });
    }

    resolveActivation(node, index)?.items.forEach((item) => {
      if (item.tone === "error") {
        issues.push({
          taskId,
          code: node.seq,
          severity: "error",
          message: `${node.seq} ${item.note ?? "has an unresolvable activation condition"}`,
        });
      }

      if (item.tone === "warning" && item.note) {
        issues.push({ taskId, code: node.seq, severity: "warning", message: `${node.seq}: ${item.note}` });
      }
    });

    if (completion && completion.minimumRequired > completion.options.length) {
      issues.push({
        taskId,
        code: node.seq,
        severity: "error",
        message: `${node.seq} requires ${completion.minimumRequired} completion conditions but has ${completion.options.length}`,
      });
    }

    resolveCompletion(node, index).memberActions.forEach((action) => {
      if (action.tone === "error" && action.note) {
        issues.push({ taskId, code: node.seq, severity: "error", message: `${node.seq}: ${action.note}` });
      }
    });

    (node.task.members ?? []).forEach((member) => {
      if (member.target === MemberTarget.ROLE && member.kind !== MemberKind.EXTERNAL && !member.assigneeRole) {
        issues.push({
          taskId,
          code: node.seq,
          severity: "warning",
          message: `${node.seq} has a ${member.role} targeted at a role, but no role is set`,
        });
      }
    });
  });

  return issues;
};

export const countSubtasks = (groups: StructuredTaskGroupDto[]) =>
  groups.reduce(
    (total, group) => total + (group.tasks ?? []).reduce((sum, task) => sum + (task.subtasks?.length ?? 0), 0),
    0,
  );
